import i18n from "i18next";
import Backend from "i18next-http-backend";
import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { initReactI18next } from "react-i18next";

import { Icons } from "@edclub/app-shared";

import { type IntroProps, IntroWithApp } from "./IntroWithApp";

import "./PlayerWrapper.scss";

interface AppWrapperProps {
  strictMode?: boolean;
  // Each app has different prop signatures, so we accept any valid React component

  App: ComponentType<any>;
  intro?: IntroProps;
  background?: string;
  i18nfile?: string;
  fontSizeUpdate?: boolean;
  sizeWatch?: boolean;
}

type Profile = {
  is_blind: boolean;
};

interface GameStats {
  valid?: boolean;
  [key: string]: unknown;
}

export function renderPlayer(
  root: { render: (children: React.ReactNode) => void },
  props: AppWrapperProps,
) {
  // todo(phil&armin): it seems this is unnecessary
  //                   and we will be removing this initialization in the near future

  i18n
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng: window.approuter.profile?.locale,
      // fallbackLng: "de_DE",
      fallbackLng: "en_US",
      interpolation: { escapeValue: false },
    });

  root.render(<PlayerWrapper {...props} />);
}

function throttle<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number,
): (...args: Args) => void {
  let lastCall = 0;
  let lastArgs: Args | null = null;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Args) {
    const now = new Date().getTime();
    lastArgs = args;

    if (now - lastCall < delay) {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        if (lastArgs) {
          fn(...lastArgs);
        }

        lastCall = new Date().getTime();
      }, delay);
    } else {
      fn(...args);
      lastCall = now;
    }
  };
}

export const PlayerWrapper = ({
  strictMode = true,
  App,
  intro,
  fontSizeUpdate = false,
  sizeWatch = false,
}: AppWrapperProps) => {
  const [profile, setProfile] = useState(
    JSON.parse(localStorage.getItem("profile") || "{}"),
  );
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (!fontSizeUpdate && !sizeWatch) {
      return;
    }

    function adjustFontSize() {
      function isHighResolution() {
        return (
          window.matchMedia("(min-resolution: 2dppx)").matches ||
          window.matchMedia("(min-resolution: 192dpi)").matches
        );
      }

      const zoomLevel = window.devicePixelRatio / (isHighResolution() ? 2 : 1);
      document.body.style.fontSize = `max(12px, min(30px, calc(2.4vw * ${zoomLevel})))`;
    }

    function onResize() {
      if (fontSizeUpdate) {
        adjustFontSize();
      }

      if (sizeWatch) {
        setSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    }

    const throttledOnResize = throttle(onResize, 500);

    if (fontSizeUpdate) {
      adjustFontSize(); // Call it initially to set the font size
    }

    window.addEventListener("resize", throttledOnResize);

    return () => window.removeEventListener("resize", throttledOnResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let content = (
    <App
      size={size}
      lesson={approuter.lesson}
      profile={approuter.profile || profile}
      onGameComplete={(data?: GameStats) => {
        const stats = data || {};
        stats.valid = true;
        approuter.finishActivityAndDone(stats);
      }}
    />
  );

  if (intro) {
    content = (
      <IntroWithApp {...intro} isBlind={profile.is_blind}>
        {content}
      </IntroWithApp>
    );
  }

  let app = (
    <Suspense fallback="loading">
      {!approuter.profile && (
        <ProfileSettings profile={profile} setProfile={setProfile} />
      )}
      {content}
    </Suspense>
  );

  if (strictMode) {
    app = <React.StrictMode>{app}</React.StrictMode>;
  }

  return app;
};

export const ProfileSettings = ({
  profile,
  setProfile,
}: {
  profile: Profile;
  setProfile: ({ is_blind }: Profile) => void;
}) => {
  const profileRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  return (
    <div ref={profileRef} className="profileSettings">
      <Icons.ProfileIcon
        onClick={() => {
          setShowDropdown(true);
        }}
      />
      <div
        className="profileSettingsDropdown"
        style={{ ...(showDropdown ? { display: "block" } : {}) }}
      >
        <div
          className="profileSettingsDropdownItem"
          onClick={() => {
            setProfile({ ...profile, is_blind: !profile.is_blind });
            localStorage.setItem(
              "profile",
              JSON.stringify({ ...profile, is_blind: !profile.is_blind }),
            );
          }}
        >
          <input
            type="checkbox"
            checked={profile.is_blind}
            onChange={() => {}}
            tabIndex={-1}
          />
          <label>Is blind</label>
        </div>
      </div>
    </div>
  );
};
